'use client';

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx } from '../utils/cx';

export type SwitchSize = 'sm' | 'md' | 'lg';
export type SwitchTone = 'brand' | 'emerald';

export interface SwitchProps {
  id?: string;
  name?: string;
  value?: string;
  /** Primary, human-readable commitment text. */
  label?: ReactNode;
  /** Secondary line rendered under the label (announced via aria-describedby). */
  description?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (...args: unknown[]) => void;
  onCheckedChange?: (checked: boolean) => void;
  onClick?: (...args: unknown[]) => void;
  disabled?: boolean;
  /** Blocks native form submission until the switch is on. */
  required?: boolean;
  /** Marks the control as failing validation. */
  invalid?: boolean;
  size?: SwitchSize;
  tone?: SwitchTone;
  /** Stretches the row so the whole width is a single hit target. */
  fullWidth?: boolean;
  /** Side of the row the track sits on, respecting the document direction. */
  controlPosition?: 'start' | 'end';
  prefPath?: string;
  storageKeyPrefix?: string;
  className?: string;
  labelClassName?: string;
  descriptionClassName?: string;
}

const DEFAULT_STORAGE_KEY_PREFIX = 'prefs:';

const SIZE_STYLES: Record<
  SwitchSize,
  { track: string; knob: string; icon: string; on: string; off: string }
> = {
  sm: {
    track: 'h-5 w-9',
    knob: 'h-3 w-3',
    icon: 'h-2 w-2',
    on: 'translate-x-5 rtl:-translate-x-5',
    off: 'translate-x-1 rtl:-translate-x-1',
  },
  md: {
    track: 'h-6 w-11',
    knob: 'h-4 w-4',
    icon: 'h-2.5 w-2.5',
    on: 'translate-x-6 rtl:-translate-x-6',
    off: 'translate-x-1 rtl:-translate-x-1',
  },
  lg: {
    track: 'h-7 w-12',
    knob: 'h-5 w-5',
    icon: 'h-3 w-3',
    on: 'translate-x-6 rtl:-translate-x-6',
    off: 'translate-x-1 rtl:-translate-x-1',
  },
};

const TONE_STYLES: Record<
  SwitchTone,
  { on: string; off: string; ring: string; check: string }
> = {
  brand: {
    on: 'bg-brand',
    off: 'bg-slate-300',
    ring: 'peer-focus-visible:ring-violet-500',
    check: 'text-violet-700',
  },
  emerald: {
    on: 'bg-emerald-600',
    off: 'bg-slate-300',
    ring: 'peer-focus-visible:ring-emerald-600',
    check: 'text-emerald-700',
  },
};

function safeLocalStorageGet(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore quota/security errors and sandboxed environments.
  }
}

function parseStoredBoolean(raw: string | null): boolean | undefined {
  if (raw === null) return undefined;

  const value = raw.trim().toLowerCase();
  if (value === '1' || value === 'true' || value === 'yes' || value === 'on') {
    return true;
  }
  if (value === '0' || value === 'false' || value === 'no' || value === 'off') {
    return false;
  }

  return undefined;
}

function formatStoredBoolean(value: boolean): string {
  return value ? '1' : '0';
}

export function Switch({
  id,
  name,
  value = 'on',
  label,
  description,
  checked,
  defaultChecked = false,
  onChange,
  onCheckedChange,
  onClick,
  disabled = false,
  required = false,
  invalid = false,
  size = 'lg',
  tone = 'brand',
  fullWidth = false,
  controlPosition = 'start',
  className,
  labelClassName,
  descriptionClassName,
  prefPath,
  storageKeyPrefix = DEFAULT_STORAGE_KEY_PREFIX,
}: SwitchProps) {
  const autoId = useId();
  const controlId = id ?? autoId;
  const labelId = `${controlId}-label`;
  const descriptionId = `${controlId}-description`;

  const storageKey = useMemo(() => {
    if (!prefPath) return '';
    const prefix =
      (storageKeyPrefix ?? DEFAULT_STORAGE_KEY_PREFIX).trim() ||
      DEFAULT_STORAGE_KEY_PREFIX;
    return `${prefix}${prefPath}`;
  }, [prefPath, storageKeyPrefix]);

  const isExternallyControlled = typeof checked === 'boolean';

  const [internalChecked, setInternalChecked] = useState<boolean>(() => {
    if (isExternallyControlled) return Boolean(checked);

    if (storageKey) {
      const stored = parseStoredBoolean(safeLocalStorageGet(storageKey));
      if (typeof stored === 'boolean') return stored;
    }

    return defaultChecked;
  });

  // Once the user has acted, neither a late-arriving default nor a storage read
  // may silently undo their choice.
  const hasInteractedRef = useRef(false);
  const previousDefaultRef = useRef(defaultChecked);

  /**
   * Keep the local visual state in sync with external updates, while still
   * allowing instant optimistic feedback on user interaction.
   */
  useEffect(() => {
    if (isExternallyControlled) {
      setInternalChecked(Boolean(checked));
      return;
    }

    if (!storageKey) return;

    const stored = parseStoredBoolean(safeLocalStorageGet(storageKey));
    if (typeof stored === 'boolean' && !hasInteractedRef.current) {
      setInternalChecked(stored);
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      const next = parseStoredBoolean(event.newValue);
      if (typeof next === 'boolean') {
        setInternalChecked(next);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [checked, isExternallyControlled, storageKey]);

  /**
   * Adopt a defaultChecked value that arrives after mount, which happens when
   * a host loads persisted answers asynchronously and passes them down as the
   * default. This is skipped after the user interacts or when storage wins.
   */
  useEffect(() => {
    if (isExternallyControlled) return;
    if (previousDefaultRef.current === defaultChecked) return;

    previousDefaultRef.current = defaultChecked;

    if (hasInteractedRef.current) return;
    if (
      storageKey &&
      typeof parseStoredBoolean(safeLocalStorageGet(storageKey)) === 'boolean'
    ) {
      return;
    }

    setInternalChecked(defaultChecked);
  }, [defaultChecked, isExternallyControlled, storageKey]);

  const isOn = internalChecked;
  const sizeStyles = SIZE_STYLES[size] ?? SIZE_STYLES.lg;
  const toneStyles = TONE_STYLES[tone] ?? TONE_STYLES.brand;

  const persist = (next: boolean) => {
    if (isExternallyControlled || !storageKey) return;
    safeLocalStorageSet(storageKey, formatStoredBoolean(next));
  };

  const notifyChange = (next: boolean) => {
    if (typeof onChange === 'function') {
      onChange(next);
    }
    if (typeof onCheckedChange === 'function') {
      onCheckedChange(next);
    }
    if (typeof onClick === 'function') {
      onClick();
    }
  };

  const handleToggle = (next: boolean) => {
    if (disabled) return;

    hasInteractedRef.current = true;

    // Update first so hosts with asynchronous state propagation still feel
    // immediate, then persist and notify external consumers.
    setInternalChecked(next);
    persist(next);
    notifyChange(next);
  };

  const hasLabel = label !== undefined && label !== null && label !== '';
  const hasDescription =
    description !== undefined && description !== null && description !== '';
  const inputName = name ?? prefPath;

  const track = (
    <span
      aria-hidden="true"
      data-state={isOn ? 'on' : 'off'}
      className={cx(
        'relative inline-flex shrink-0 items-center overflow-hidden rounded-full border border-black/10 shadow-inner',
        'transition-[background-color,filter,box-shadow,transform] duration-200 motion-reduce:transition-none',
        'group-hover:brightness-95 peer-active:scale-[0.98]',
        'peer-focus-visible:ring-4 peer-focus-visible:ring-offset-2',
        sizeStyles.track,
        isOn ? toneStyles.on : toneStyles.off,
        toneStyles.ring,
        invalid && 'ring-2 ring-rose-500 ring-offset-2',
        'forced-colors:border-[ButtonText] forced-colors:bg-[ButtonFace]',
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          'inline-flex transform items-center justify-center rounded-full bg-white shadow-md ring-1 ring-slate-900/10',
          'transition-[transform,box-shadow] duration-200 motion-reduce:transition-none',
          sizeStyles.knob,
          isOn ? sizeStyles.on : sizeStyles.off,
        )}
      >
        <svg
          viewBox="0 0 12 12"
          fill="none"
          className={cx(
            'transition-[opacity,transform] duration-150 motion-reduce:transition-none',
            sizeStyles.icon,
            toneStyles.check,
            isOn ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
          )}
        >
          <path
            d="M2 6.25 4.65 8.5 10 3.25"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </span>
  );

  const text =
    hasLabel || hasDescription ? (
      <span className={cx('min-w-0', fullWidth && 'flex-1')}>
        {hasLabel ? (
          <span
            id={labelId}
            className={cx('block text-sm text-secondary', labelClassName)}
          >
            {label}
          </span>
        ) : null}
        {hasDescription ? (
          <span
            id={descriptionId}
            className={cx(
              'mt-0.5 block text-xs leading-5 text-slate-500',
              descriptionClassName,
            )}
          >
            {description}
          </span>
        ) : null}
      </span>
    ) : null;

  return (
    <label
      htmlFor={controlId}
      data-state={isOn ? 'on' : 'off'}
      data-disabled={disabled ? 'true' : undefined}
      data-invalid={invalid ? 'true' : undefined}
      data-required={required ? 'true' : undefined}
      className={cx(
        'group items-center gap-3 touch-manipulation',
        fullWidth ? 'flex min-h-11 w-full min-w-0' : 'inline-flex',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      {/* Hidden but native: preserves label activation, keyboard support, form
          serialization, and required-field validation. */}
      <input
        id={controlId}
        name={inputName}
        value={value}
        type="checkbox"
        className="peer sr-only"
        role="switch"
        aria-checked={isOn}
        aria-label={hasLabel ? undefined : 'Toggle'}
        aria-labelledby={hasLabel ? labelId : undefined}
        aria-describedby={hasDescription ? descriptionId : undefined}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        required={required || undefined}
        disabled={disabled}
        checked={isOn}
        onChange={(event) => handleToggle(event.target.checked)}
      />

      {controlPosition === 'end' ? (
        <>
          {text}
          {track}
        </>
      ) : (
        <>
          {track}
          {text}
        </>
      )}
    </label>
  );
}

Switch.displayName = 'Switch';